import React, { useState, useEffect, useMemo } from "react";
import { FiSearch } from "react-icons/fi";
import axios from "axios";
import apiKey from "../../../config/apiKey";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { useTranslation } from "react-i18next";

function MyStudent() {
  const { t } = useTranslation();
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const teacher = JSON.parse(localStorage.getItem("edo-teacher"));
  const userId = teacher ? teacher.teacher.id : "";
  const [loading, setLoading] = useState(true);
  const [sortOption, setSortOption] = useState("Newest");
  const [expandedRow, setExpandedRow] = useState(null);

  useEffect(() => {
    setLoading(true);
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${apiKey}/api/edolutions/teacher/read-notifications-all/${userId}`,
          {
            headers: {
              Authorization: `Bearer ${teacher?.token}`,
            },
          }
        );
        setData(response.data.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };

    if (userId) {
      fetchData();
    }
  }, [userId, teacher?.token]);

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSortChange = (e) => {
    setSortOption(e.target.value);
  };

  // ✅ 1) Group by student_id so each mentee appears only once
  const groupedData = useMemo(() => {
    const map = new Map();

    data.forEach((item) => {
      const key = item.student_id; // group key

      if (!map.has(key)) {
        map.set(key, {
          student_id: item.student_id,
          student_name: item.student_name,
          // representative fields (first session ka data)
          class_name: item.class_name,
          subjects: item.subjects,
          type_name: item.type_name,
          // all sessions for detail view
          sessions: [item],
          // for sorting (latest session time)
          latest_created_at: item.created_at,
        });
      } else {
        const existing = map.get(key);
        existing.sessions.push(item);
        // update latest_created_at if this session is newer
        if (
          item.created_at &&
          new Date(item.created_at) > new Date(existing.latest_created_at)
        ) {
          existing.latest_created_at = item.created_at;
        }
      }
    });

    return Array.from(map.values());
  }, [data]);

  // ✅ 2) Sort on latest session date per student
  const sortedData = [...groupedData].sort((a, b) => {
    if (sortOption === "Newest") {
      return new Date(b.latest_created_at) - new Date(a.latest_created_at);
    } else if (sortOption === "Oldest") {
      return new Date(a.latest_created_at) - new Date(b.latest_created_at);
    }
    return 0;
  });

  // ✅ 3) Filter on student name / class / subjects
  const filteredData = sortedData.filter((item) => {
    const term = searchTerm.toLowerCase();
    return (
      (item.student_name || "").toLowerCase().includes(term) ||
      (item.class_name || "").toLowerCase().includes(term) ||
      (item.subjects || "").toLowerCase().includes(term)
    );
  });

  const totalItems = filteredData.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage) || 1;

  const handlePageChange = (pageNumber) => {
    setCurrentPage(Math.min(Math.max(1, pageNumber), totalPages));
  };

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages));
  };

  const handlePrevPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1));
  };

  const handleItemsPerPageChange = (event) => {
    const selectedItemsPerPage = parseInt(event.target.value, 10);
    setItemsPerPage(selectedItemsPerPage);
    setCurrentPage(1);
  };

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = Math.min(startIndex + itemsPerPage, totalItems);
  const paginatedData = filteredData.slice(startIndex, endIndex);

  // Time format helper
  function formatTime(time) {
    if (!time) return "";
    let [hours, minutes] = time.split(":");
    let period = "AM";

    hours = parseInt(hours, 10);

    if (hours >= 12) {
      period = "PM";
      if (hours > 12) {
        hours -= 12;
      }
    } else if (hours === 0) {
      hours = 12;
    }

    return `${hours}:${minutes} ${period}`;
  }

  const handleToggleDetails = (index) => {
    setExpandedRow(expandedRow === index ? null : index);
  };

  return (
    <>
      <div className="flex items-center xl:p-5 sm:p-0 justify-center">
        <div className="w-full border rounded-3xl shadow-xl p-5">
          <div className="mt-2">
            <div className="flex flex-col md:flex-row md:items-center justify-between p-2">
              <div className="flex justify-between">
                <p className="text-[22px] font-bold">{t("teacherDash.myStudent.title")}</p>
              </div>
              <div className="flex flex-col gap-4 max-md:w-[50%] md:flex-row md:items-center">
                <div className="relative border shadow-md rounded-md flex items-center px-5 py-2">
                  <FiSearch className="h-5 w-5 absolute text-[#5963F5]" />
                  <input
                    type="text"
                    className="outline-none pl-8"
                    placeholder={t("teacherDash.common.search")}
                    value={searchTerm}
                    onChange={handleSearch}
                  />
                </div>
                <div className="relative ml-2">
                  <select
                    className="border shadow-md rounded-md cursor-pointer px-5 py-2 outline-none pl-2 pr-8 text-sm"
                    value={sortOption}
                    onChange={handleSortChange}
                  >
                    <option value="Newest">{t("teacherDash.homeTution.sortByNewest")}</option>
                    <option value="Oldest">{t("teacherDash.homeTution.sortByOldest")}</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="mt-5 max-sm:max-w-xs overflow-x-auto">
              <table className="w-full text-sm overflow-x-auto text-left text-gray-500 border-b table-auto">
                <thead className="text-sm text-[#B5B7C0] font-medium border-b">
                  <tr className="border-b">
                    <td className="py-5 px-1">{t("teacherDash.myStudent.colMenteeName")}</td>
                    <td className="py-5 px-1">{t("teacherDash.myStudent.colProfession")}</td>
                    <td className="py-5 px-1">{t("teacherDash.myStudent.colSpecialization")}</td>
                    <td className="py-5 px-1">{t("teacherDash.myStudent.colType")}</td>
                    <td className="py-5 px-1">{t("teacherDash.homeTution.colDetails")}</td>
                  </tr>
                </thead>
                <tbody className="text-black text-sm font-medium">
                  {loading ? (
                    <tr>
                      <td colSpan="6" className="py-5 px-1 text-center">
                        <div className="fixed inset-0 flex flex-col items-center justify-center bg-gray-200 bg-opacity-5 backdrop-blur-sm z-50">
                          <div className="w-12 h-12 border-4 border-t-4 border-gray-200 border-solid rounded-full animate-spin border-t-[#EE5F2B]"></div>
                          <p className="mt-4 text-lg font-semibold text-[#EE5F2B]">
                            {t("teacherDash.homeTution.loadingText")}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : paginatedData.length === 0 ? (
                    <tr>
                      <td colSpan="6" className="py-5 px-1 text-center">
                        <p className="text-gray-500">{t("teacherDash.myStudent.noMenteesFound")}</p>
                      </td>
                    </tr>
                  ) : (
                    paginatedData.map((item, index) => (
                      <React.Fragment key={item.student_id || index}>
                        <tr className="border-b">
                          <td className="py-5 px-1">{item.student_name}</td>
                          <td className="py-5 px-1">{item.class_name}</td>
                          <td className="py-5 px-1">{item.subjects}</td>
                          <td className="py-5 px-1 capitalize">
                            {item.type_name}
                          </td>
                          <td className="py-5 px-1">
                            <button
                              className="text-blue-500 underline"
                              onClick={() => handleToggleDetails(index)}
                            >
                              {expandedRow === index ? (
                                <AiOutlineEyeInvisible className="text-lg text-[#EE5F2B]" />
                              ) : (
                                <AiOutlineEye className="text-lg text-[#EE5F2B]" />
                              )}
                            </button>
                          </td>
                        </tr>

                        {expandedRow === index && (
                          <tr>
                            <td colSpan="6">
                              <table className="w-full text-sm text-left text-gray-500 rounded-lg shadow-lg table-auto">
                                <thead className="text-sm text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54] font-medium">
                                  <tr>
                                    <td className="py-3 px-1">{t("teacherDash.common.day")}</td>
                                    <td className="py-3 px-1">{t("teacherDash.homeTution.timing")}</td>
                                    <td className="py-3 px-1">{t("teacherDash.homeTution.duration")}</td>
                                    <td className="py-3 px-1">{t("teacherDash.common.payment")}</td>
                                  </tr>
                                </thead>
                                <tbody className="text-black text-sm font-medium">
                                  {item.sessions.map((session, sIdx) => (
                                    <tr key={session.id || sIdx}>
                                      <td className="py-3 px-1 capitalize">
                                        {session.day_name
                                          ? session.day_name
                                          : t("teacherDash.homeTution.notDefined")}
                                      </td>
                                      <td className="py-3 px-1">
                                        {session.start_time &&
                                        session.end_time
                                          ? `${formatTime(
                                              session.start_time
                                            )} to ${formatTime(
                                              session.end_time
                                            )}`
                                          : session.timeslot_id}
                                      </td>
                                      <td className="py-3 px-1">
                                        {session.days} {session.duration}
                                      </td>
                                      <td className="py-3 px-1">
                                        {t("teacherDash.homeTution.currencyPrefix")}{session.payment}
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            <div className="flex flex-wrap gap-5 justify-between items-center w-full text-sm mt-4 pb-4 px-2">
              <div className="flex items-center gap-x-2 text-gray-400">
                <span>
                  <select
                    className="text-sm border-none text-black bg-[#F5F5F5] rounded-lg"
                    value={itemsPerPage}
                    onChange={handleItemsPerPageChange}
                  >
                    {[10, 20, 30, 40, 50, 100].map((perPage) => (
                      <option key={perPage} value={perPage}>
                        {perPage}
                      </option>
                    ))}
                  </select>
                </span>
                <span>{t("teacherDash.homeTution.showingData")}</span>
                <span>
                  {t("teacherDash.myStudent.entriesRangeValue", { range: totalItems === 0 ? "0-0" : `${startIndex + 1}-${endIndex}`, total: totalItems })}
                </span>
              </div>
              <div className="flex items-center gap-x-2 text-gray-400">
                <button
                  className="text-base text-black bg-[#F5F5F5] rounded-lg px-2 py-1"
                  onClick={handlePrevPage}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                <div className="flex items-center gap-x-2 text-gray-400">
                  {[...Array(Math.min(5, totalPages || 1))].map((_, index) => {
                    const pageNumber = Math.max(currentPage - 2 + index, 1);
                    const isActive = pageNumber === currentPage;
                    return (
                      <button
                        key={pageNumber}
                        className={`text-base rounded-lg px-3 py-1 ${
                          isActive
                            ? "text-white bg-gradient-to-r from-[#EE5F2B] to-[#2A3A54]"
                            : "text-black bg-[#F5F5F5]"
                        }`}
                        onClick={() => handlePageChange(pageNumber)}
                        disabled={pageNumber < 1 || pageNumber > totalPages}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                  <span>{t("teacherDash.homeTution.pagesOf", { count: totalPages || 1 })}</span>
                </div>
                <button
                  className="text-base text-black bg-[#F5F5F5] rounded-lg px-2 py-1"
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                >
                  &gt;
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default MyStudent;
